import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UnsavedChangesModal from '../components/UnsavedChangesModal.js';

const UnsavedChangesContext = createContext(null);

export const UnsavedChangesProvider = ({ children }) => {
  const { t } = useTranslation();
  const guardsRef = useRef(new Map());
  const [version, setVersion] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingGuardId, setPendingGuardId] = useState(null);
  const [modalAction, setModalAction] = useState(null);
  const [modalError, setModalError] = useState(null);

  const bumpVersion = useCallback(() => setVersion((value) => value + 1), []);

  const registerUnsavedChangesGuard = useCallback((id, guard) => {
    guardsRef.current.set(id, guard);
    bumpVersion();

    return () => {
      guardsRef.current.delete(id);
      bumpVersion();
    };
  }, [bumpVersion]);

  const getActiveGuardEntry = useCallback(() => {
    for (const [id, guard] of guardsRef.current.entries()) {
      if (guard?.when) return [id, guard];
    }
    return null;
  }, []);

  const hasActiveGuard = useCallback(
    () => Array.from(guardsRef.current.values()).some((g) => g?.when),
    []
  );

  // Resolve the current guard from the ref by id so we always use the freshest
  // handlers (avoids stale closures when handlers update between modal open & click).
  const currentPendingGuard = pendingGuardId ? guardsRef.current.get(pendingGuardId) : null;

  useEffect(() => {
    if (!hasActiveGuard()) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [version, hasActiveGuard]);

  const requestUnsavedAction = useCallback((action) => {
    const entry = getActiveGuardEntry();
    if (!entry) {
      action?.();
      return true;
    }
    setPendingGuardId(entry[0]);
    setPendingAction(() => action);
    setModalError(null);
    return false;
  }, [getActiveGuardEntry]);

  const clearPending = useCallback(() => {
    setPendingAction(null);
    setPendingGuardId(null);
    setModalAction(null);
    setModalError(null);
  }, []);

  // NOTE: react-router v6 BrowserRouter doesn't expose useBlocker (data router only).
  // In-app navigations are guarded by runNavigation/requestUnsavedAction at the call sites
  // (sidebar, admin items, settings/analytics/add-bot click handlers). Browser back/forward
  // and full unloads are caught by the beforeunload listener above.

  const continuePending = useCallback(() => {
    const action = pendingAction;
    setPendingAction(null);
    setPendingGuardId(null);
    setModalAction(null);
    setModalError(null);
    action?.();
  }, [pendingAction]);

  const handleSave = useCallback(async () => {
    if (!currentPendingGuard) return;
    setModalAction('save');
    setModalError(null);
    try {
      const result = await currentPendingGuard.onSave?.();
      if (result === false) {
        setModalAction(null);
        return;
      }
      if (typeof result === 'string' && result.length > 0) {
        setModalError(result);
        setModalAction(null);
        return;
      }
      continuePending();
    } catch (err) {
      console.error('Unsaved-changes save handler failed:', err);
      setModalError(err?.message || t('unsaved_save_failed'));
      setModalAction(null);
    }
  }, [continuePending, currentPendingGuard]);

  const handleDiscard = useCallback(async () => {
    if (!currentPendingGuard) return;
    setModalAction('discard');
    setModalError(null);
    try {
      await currentPendingGuard.onDiscard?.();
      continuePending();
    } catch (err) {
      console.error('Unsaved-changes discard handler failed:', err);
      setModalError(err?.message || t('unsaved_discard_failed'));
      setModalAction(null);
    }
  }, [continuePending, currentPendingGuard]);

  const handleClose = useCallback(() => {
    clearPending();
  }, [clearPending]);

  const value = useMemo(() => ({
    registerUnsavedChangesGuard,
    requestUnsavedAction,
  }), [registerUnsavedChangesGuard, requestUnsavedAction]);

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <UnsavedChangesModal
        open={!!currentPendingGuard}
        onSave={handleSave}
        onDiscard={handleDiscard}
        onClose={handleClose}
        saving={modalAction === 'save'}
        discarding={modalAction === 'discard'}
        title={currentPendingGuard?.title}
        message={currentPendingGuard?.message}
        error={modalError}
      />
    </UnsavedChangesContext.Provider>
  );
};

/** Like useUnsavedChanges, but null outside the provider (for parts that also render on their own, e.g. in tests). */
export const useOptionalUnsavedChanges = () => useContext(UnsavedChangesContext);

export const useUnsavedChanges = () => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChanges must be used within UnsavedChangesProvider');
  }
  return context;
};
